<script setup lang="ts">
import type { OperationModel } from "~/core/models/OperationModel.interface";
import type { OperationModelType } from "~/core/models/OperationModelType.enum";
import type { CampaignDateRangeI } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3DateRange.vue";
import CampaignFunnelStep3DateRange from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3DateRange.vue";
import type { CampaignInclusionPeriodsI } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3InclusionPeriods.vue";
import CampaignFunnelStep3InclusionPeriods from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3InclusionPeriods.vue";
import { CUSTOM_INPUT_PARAMETERS_NAMES } from "~/modules/campaign/models/CampaignOperationMgr.enum";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";

export interface CampaignFunnelStep3 {
  dateRange: CampaignDateRangeI;
  inclusionPeriods: CampaignInclusionPeriodsI;
}

interface Props {
  modelValue: CampaignFunnelStep3;
  canEdit: boolean;
  operationModels: OperationModel[];
  operationModelType?: OperationModelType;
  firmware?: Firmware;
}

const props = withDefaults(defineProps<Props>(), {
  operationModelType: undefined,
});

const emit = defineEmits(["update:modelValue"]);

const campaignFunnelStep3 = computed({
  get() {
    return props.modelValue;
  },
  set(campaignFunnelStep3?: CampaignFunnelStep3) {
    emit("update:modelValue", campaignFunnelStep3);
  },
});

const campaignFunnelStep3DateRangeRef: Ref<
  typeof CampaignFunnelStep3DateRange | undefined
> = ref();
const campaignFunnelStep3InclusionPeriodsRef: Ref<
  typeof CampaignFunnelStep3InclusionPeriods | undefined
> = ref();
const windowWidth = ref(window.innerWidth);

const onResize = () => {
  windowWidth.value = window.innerWidth;
};

// Magic numbers are used here to make the component responsive.
const containerClass = computed(() => {
  let containerClass = "w-full";
  if (windowWidth.value > 1700) {
    containerClass = "w-3/5";
  } else if (windowWidth.value > 1400) {
    containerClass = "w-4/5";
  }
  return containerClass;
});

// display the inclusion periods if at least one operation models of the selected operation type can manage them
const displayInclusionPeriods = computed(() => {
  for (const operationModel of props.operationModels) {
    if (
      operationModel.type === props.operationModelType &&
      (!props.firmware ||
        operationModel.deviceTypes?.includes(props.firmware.range))
    ) {
      for (const customInputParameter of operationModel.customInputParameters) {
        if (
          customInputParameter.name ===
          CUSTOM_INPUT_PARAMETERS_NAMES.INCLUSION_PERIODS
        ) {
          return true;
        }
      }
    }
  }

  return false;
});

const isValid: ComputedRef<boolean> = computed(() => {
  return (
    campaignFunnelStep3DateRangeRef.value?.isValid &&
    (!displayInclusionPeriods.value ||
      campaignFunnelStep3InclusionPeriodsRef?.value?.isValid)
  );
});

onMounted(() => {
  window.addEventListener("resize", onResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
});

defineExpose({
  validate: async () =>
    Promise.all([
      campaignFunnelStep3DateRangeRef.value?.validate().catch(() => false),
      campaignFunnelStep3InclusionPeriodsRef.value
        ?.validate()
        .catch(() => false),
    ]),
  isValid,
});
</script>

<template>
  <div :class="containerClass">
    <campaign-operation-mgr-funnel-step3-date-range
      ref="campaignFunnelStep3DateRangeRef"
      v-model="campaignFunnelStep3.dateRange"
      :can-edit="canEdit"
    />
    <campaign-operation-mgr-funnel-step3-inclusion-periods
      v-if="displayInclusionPeriods"
      ref="campaignFunnelStep3InclusionPeriodsRef"
      v-model="campaignFunnelStep3.inclusionPeriods"
      :can-edit="canEdit"
    />
  </div>
</template>
